/**
 * Mounts the operator-supplied code-injection snippets from settings/website.
 *
 *   headerCode     -> end of <head>
 *   bodyStartCode  -> start of <body>
 *   bodyEndCode    -> end of <body>
 *
 * Intended for GTM / GA4 / Meta Pixel / Search Console verification.
 *
 * SECURITY: this executes markup an admin stored in Firestore, verbatim, in
 * every visitor's browser. That is the point of the feature and there is no
 * safe way to both "allow arbitrary analytics tags" and "sanitise the script
 * out of them" — so the control is upstream: firestore.rules restricts writes
 * to holders of the `admin` custom claim and caps each field at 20KB. Treat the
 * admin claim as equivalent to deploy access and grant it accordingly.
 */
import { useEffect } from "react";
import { getWebsiteSettings } from "@/services/settings.service";

const MARKER = "data-cs-injected";

/**
 * Parse an HTML string and attach its nodes to `target`.
 *
 * Scripts are rebuilt element-by-element rather than assigned via innerHTML:
 * the HTML parser marks scripts inserted that way as "already started" and
 * refuses to run them, so innerHTML would inject a GTM snippet that silently
 * never fires. Recreating the node is what makes it execute.
 */
function injectSnippet(target, html, slot, { prepend = false } = {}) {
    if (!html || !html.trim()) return [];

    const template = document.createElement("template");
    template.innerHTML = html;

    const injected = [];
    for (const node of Array.from(template.content.childNodes)) {
        let el;
        if (node.nodeName === "SCRIPT") {
            el = document.createElement("script");
            for (const { name, value } of Array.from(node.attributes)) {
                el.setAttribute(name, value);
            }
            el.text = node.textContent;
        } else {
            el = node.cloneNode(true);
        }

        if (el.nodeType === Node.ELEMENT_NODE) el.setAttribute(MARKER, slot);

        if (prepend && target.firstChild) target.insertBefore(el, target.firstChild);
        else target.appendChild(el);

        injected.push(el);
    }
    return injected;
}

export default function CodeInjection() {
    useEffect(() => {
        let cancelled = false;
        let injected = [];

        (async () => {
            let settings;
            try {
                settings = await getWebsiteSettings();
            } catch (err) {
                console.warn("[code-injection] settings unavailable:", err.message);
                return;
            }
            // The component may have unmounted (or a hot reload landed) while
            // the settings read was in flight — bail rather than leak nodes.
            if (cancelled || !settings) return;

            injected = [
                ...injectSnippet(document.head, settings.headerCode, "header"),
                ...injectSnippet(document.body, settings.bodyStartCode, "body-start", {
                    prepend: true,
                }),
                ...injectSnippet(document.body, settings.bodyEndCode, "body-end"),
            ];
        })();

        return () => {
            cancelled = true;
            injected.forEach((el) => el.remove());
        };
    }, []);

    return null;
}
